import { optionService } from "../option";
import { CommandWithFollowup, LayoutsState, MaterialStepData, Step, StepService, VariantResource } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { AssetNotFoundError, OptionNotFoundError } from "../../util/exception";

class MaterialStepService implements StepService<MaterialStepData> {
    async init(
        stepData: Step<MaterialStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<null | CommandWithFollowup> {
        const option = stepData.option;
        if (!option) {
            throw new OptionNotFoundError(stepData);
        }
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            const defaultVariant = optionService.getDefaultVariant(option);
            if (defaultVariant) {
                return await this.selectVariantLambda(stepData, defaultVariant, workflowManager, () => {});
            }
        }
        return null;
    }

    private async reload(
        stepData: Step<MaterialStepData>,
        workflowManager: WorkflowManager,
        reducerState: LayoutsState,
    ) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;
        if (!option) throw new OptionNotFoundError(stepData);
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (variantId) {
                const variant = option.variants?.find((variant: VariantResource) => variant.id === variantId);
                if (variant) {
                    const material = variant.material;
                    const regionElements = elementsForStep.map((el) => ({
                        id: el.id,
                        region: el.stepRegion,
                        regionIndex: el.stepRegionIndex,
                    }));
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            const modelContainer = workflowManager.getModelContainer();
                            if (modelContainer) {
                                const variantPromises = stepData.data.targetMaterials.map((matName) => {
                                    return modelContainer.applyMaterialVariant(
                                        matName,
                                        option.id || "",
                                        material || {},
                                    );
                                });
                                Promise.all(variantPromises).then(() =>
                                    workflowManager.setMandatoryFulfilled(stepData.stepName, true),
                                );
                            }
                        },
                    );
                }
            }
        }
    }

    async selectVariant(
        step: Step<MaterialStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setApplying: (status: boolean) => void,
    ) {
        const result = await this.selectVariantLambda(step, variant, workflowManager, setApplying);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async selectVariantLambda(
        step: Step<MaterialStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setApplying: (status: boolean) => void,
    ): Promise<CommandWithFollowup | null> {
        const modelContainer = workflowManager.getModelContainer();
        setApplying(true);
        const material = variant.material;
        if (!material) {
            setApplying(false);
            throw new AssetNotFoundError(variant);
        }
        return {
            command: undefined,
            followup: async () => {
                await workflowManager.setSelectionsAndElements(step.stepName, [variant], [], async () => {
                    try {
                        if (modelContainer) {
                            step.data.targetMaterials.forEach((matName) => {
                                step.option &&
                                    modelContainer.applyMaterialVariant(matName, step.option.id || "", material);
                            });
                        }
                        workflowManager.setMandatoryFulfilled(step.stepName, true);
                    } finally {
                        setApplying(false);
                    }
                });
            },
        };
    }
}

export const materialStepService = new MaterialStepService();
